import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Package } from 'lucide-react';

const fmtC = (n, c) => `${Number(n).toLocaleString(undefined, { maximumFractionDigits: c === 'FUZZY' ? 0 : 2 })} ${c}`;
const STATUS_STYLE = {
  active: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  pending_approval: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  sold: 'bg-red-500/15 text-red-300 border-red-500/30',
  pending: 'bg-slate-700/50 text-slate-300 border-slate-600',
  rejected: 'bg-red-900/30 text-red-400 border-red-800/50',
  removed: 'bg-slate-800 text-slate-500 border-slate-700',
};

export default function MyShopDialog({ open, onOpenChange, onChanged }) {
  const [data, setData] = useState(null);
  const [trackingDrafts, setTrackingDrafts] = useState({});
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(() => {
    axios.get(`${API}/shop/my`).then(r => setData(r.data)).catch(() => {});
  }, []);
  useEffect(() => { if (open) refresh(); }, [open, refresh]);

  const act = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); refresh(); onChanged?.(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Action failed'); }
    finally { setBusy(false); }
  };

  const listings = data?.listings || [];
  const offers = data?.incoming_offers || [];
  const sales = data?.sales || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="my-shop-dialog" className="bg-slate-900 border-slate-700 w-[calc(100%-2rem)] max-w-sm max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-lg text-white">My Shop</DialogTitle>
          <DialogDescription className="text-slate-500 text-xs">
            Platform cut on your sales: {data?.seller_fee_pct ?? '—'}% · paid out when the buyer confirms delivery
          </DialogDescription>
        </DialogHeader>

        {offers.length > 0 && (
          <div className="space-y-2">
            <p className="text-[10px] font-bold text-amber-300 uppercase">Incoming offers ({offers.length})</p>
            {offers.map(o => (
              <div key={o.offer_id} data-testid={`incoming-offer-${o.offer_id.slice(0, 8)}`} className="rounded-xl bg-amber-500/5 border border-amber-500/20 p-2.5 space-y-1.5">
                <p className="text-xs text-white font-semibold">{o.listing_title}</p>
                <p className="text-[11px] text-slate-400">@{o.buyer_username} offers <span className="text-amber-300 font-bold">{fmtC(o.amount, o.currency)}</span></p>
                <div className="grid grid-cols-2 gap-2">
                  <Button data-testid={`offer-accept-${o.offer_id.slice(0, 8)}`} size="sm" disabled={busy} onClick={() => act(() => axios.post(`${API}/shop/offers/${o.offer_id}/accept`), 'Offer accepted — sale in escrow!')} className="bg-emerald-600 hover:bg-emerald-500 h-8 text-xs">Accept</Button>
                  <Button data-testid={`offer-decline-${o.offer_id.slice(0, 8)}`} size="sm" variant="outline" disabled={busy} onClick={() => act(() => axios.post(`${API}/shop/offers/${o.offer_id}/decline`), 'Offer declined')} className="border-slate-600 text-slate-300 h-8 text-xs">Decline</Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {sales.length > 0 && (
          <div className="space-y-2">
            <p className="text-[10px] font-bold text-cyan-300 uppercase">Sales to ship ({sales.length})</p>
            {sales.map(s => (
              <div key={s.escrow_id} data-testid={`sale-${s.escrow_id.slice(0, 8)}`} className="rounded-xl bg-slate-800/50 border border-slate-700 p-2.5 space-y-1.5">
                <p className="text-xs text-white font-semibold flex items-center gap-1.5"><Package className="w-3.5 h-3.5 text-cyan-400" /> {s.prize_description}</p>
                <p className="text-[11px] text-slate-400">
                  {fmtC(s.escrow_amount, s.currency)} in escrow · you receive <span className="text-emerald-300 font-bold">{fmtC(s.escrow_amount * (1 - (s.seller_fee_pct ?? 9.85) / 100), s.currency)}</span>
                </p>
                {s.status === 'shipped' ? (
                  <p className="text-[10px] text-emerald-300">📦 Shipped — tracking {s.tracking_number || 'N/A'} · awaiting buyer confirmation</p>
                ) : s.status === 'disputed' ? (
                  <p className="text-[10px] text-red-400">⚠️ Disputed — admin reviewing</p>
                ) : !s.shipping_info ? (
                  <p className="text-[10px] text-amber-300">⏳ Waiting for the buyer to submit their shipping address</p>
                ) : (
                  <div className="space-y-1.5">
                    <p className="text-[10px] text-slate-400">Ship to: {s.shipping_info.full_name} — {s.shipping_info.city || ''} {s.shipping_info.country || ''}</p>
                    <p className="text-[9px] text-amber-300/90">📦 Ship signature-required — mandatory. Without it, the sale is final and non-delivery is on you.</p>
                    <Input data-testid={`sale-tracking-${s.escrow_id.slice(0, 8)}`} placeholder="Tracking number" value={trackingDrafts[s.escrow_id] || ''} onChange={e => setTrackingDrafts(d => ({ ...d, [s.escrow_id]: e.target.value }))} className="bg-slate-800 border-slate-700 text-white text-xs h-9" />
                    <Button data-testid={`sale-ship-${s.escrow_id.slice(0, 8)}`} size="sm" disabled={busy || !(trackingDrafts[s.escrow_id] || '').trim()} onClick={() => act(() => axios.post(`${API}/escrow/${s.escrow_id}/action`, { action: 'mark_shipped', tracking_number: trackingDrafts[s.escrow_id].trim() }), 'Marked as shipped!')} className="w-full bg-cyan-600 hover:bg-cyan-500 h-8 text-xs">Mark shipped</Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="space-y-2">
          <p className="text-[10px] font-bold text-slate-400 uppercase">My listings ({listings.length})</p>
          {listings.length === 0 && <p className="text-xs text-slate-500 text-center py-3">No listings yet — hit "Sell a card" to start</p>}
          {listings.map(l => (
            <div key={l.listing_id} data-testid={`my-listing-${l.listing_id.slice(0, 8)}`} className="rounded-xl bg-slate-800/40 border border-slate-700/60 p-2.5 flex items-center gap-2">
              <div className="flex-1 min-w-0">
                <p className="text-xs text-white font-semibold truncate">{l.title}</p>
                <p className="text-[10px] text-slate-500">
                  {l.payment_mode === 'fuzzy' ? fmtC(l.price_fuzzy, 'FUZZY') : fmtC(l.price_xrp, 'XRP')}
                  {l.payment_mode === 'both' && ` / ${fmtC(l.price_fuzzy, 'FUZZY')}`} · qty {l.quantity}
                  {l.rejected_reason ? ` · ${l.rejected_reason}` : ''}
                </p>
              </div>
              <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded border flex-shrink-0 ${STATUS_STYLE[l.status] || STATUS_STYLE.pending}`}>
                {l.status === 'pending_approval' ? 'PENDING' : l.status.toUpperCase()}
              </span>
              {['active', 'pending_approval'].includes(l.status) && (
                <button data-testid={`my-listing-remove-${l.listing_id.slice(0, 8)}`} disabled={busy} onClick={() => act(() => axios.delete(`${API}/shop/listings/${l.listing_id}`), 'Listing removed')} className="text-[10px] text-red-400 flex-shrink-0">Remove</button>
              )}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
